import { Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@tellescope/ui";
import { ensureHttps } from "@tellescope/utils";
import type { CalendarEvent } from "@tellescope/types-models";

export interface VideoCallButtonProps {
  event: CalendarEvent;
  videoType: "twilio" | "zoom" | "external" | "healthie";
  videoUrl?: string;
  size?: "sm" | "default";
  disabled?: boolean;
  onTwilioClick?: (event: CalendarEvent) => void | Promise<void>;
  className?: string;
}

/**
 * Unified video call button component that handles all video integration types.
 * Renders either a Button (for Twilio/Chime) or a link (for external video).
 *
 * @param event - The calendar event
 * @param videoType - Type of video integration
 * @param videoUrl - URL for external video types
 * @param size - Button size variant ("sm" for cards, "default" for pages)
 * @param disabled - Whether the button is disabled
 * @param onTwilioClick - Click handler for Twilio/Chime in-app video
 * @param className - Additional CSS classes
 */
export const VideoCallButton = ({
  event,
  videoType,
  videoUrl,
  size = "default",
  disabled = false,
  onTwilioClick,
  className,
}: VideoCallButtonProps) => {
  const { t } = useTranslation("events");

  // For Twilio/Chime, render a Button component
  if (videoType === "twilio") {
    return (
      <Button
        variant="default"
        size={size}
        disabled={disabled}
        onClick={() => onTwilioClick?.(event)}
        className={cn(size === "default" ? "w-full h-12" : "mt-2 h-auto py-1.5 px-3 text-xs", className)}
      >
        <Video className={cn(size === "default" ? "h-5 w-5 mr-2" : "h-3.5 w-3.5 mr-1.5")} />
        {t("Join video call")}
      </Button>
    );
  }

  // For external video (Zoom, external URL, Healthie), render a link
  const externalUrl = videoUrl ? ensureHttps(videoUrl) : "#";

  if (size === "sm") {
    return (
      <a
        href={externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-medium",
          "text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors",
          disabled && "opacity-50 pointer-events-none",
          className
        )}
      >
        <Video className="h-3.5 w-3.5" />
        <span>{t("Join video call")}</span>
      </a>
    );
  }

  return (
    <a
      href={externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center justify-center gap-2 w-full h-12",
        "bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
    >
      <Video className="h-5 w-5" />
      {t("Join video call")}
    </a>
  );
};
